import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  FormControl,
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { TuiFileLike } from '@taiga-ui/kit';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG } from '@src/core';
import { maxFilesLength } from '@src/utils';
import { ean13Validator } from '@src/app/modules/common/barcode/validators';

import { TuiFileLikeWithId } from '../../types';
import {
  LoyaltyProgramProductInput,
  LoyaltyProgramProductTermQrCodeImage,
  LoyaltyProgramProductTerms,
  LoyaltyProgramProductTermUniqueCodes,
} from '../../../models';
import { AbstractStepComponent } from '../abstract-step/abstract-step.component';

import { validateQrCodeFields } from './validators/validateQrCodeFields';
import {
  DiscountTypeItem,
  TermsStepFormData,
  TermsStepFormDataControls,
  TermsStepFormGroupDataControls,
} from './types';
import { DISCOUNT_TYPE_NAMES } from './constants';

@Component({
  selector: 'lp-product-terms-step',
  templateUrl: './terms-step.component.html',
  styleUrls: ['./terms-step.component.scss'],
  // TODO: поменял стратегию на `default` т.к. `cdr.markForCheck()` при вызове из диалога, где лежит форма, не работает
  changeDetection: ChangeDetectionStrategy.Default,
})
export class TermsStepComponent extends AbstractStepComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly translateService = inject(TranslateService);

  readonly maxFileSize = APP_CONFIG.fileSizeMax.image;
  readonly acceptImageFileMask = APP_CONFIG.fileTypes.image.join(',');
  readonly acceptExcelFileMask = APP_CONFIG.fileTypes.excelDocument.join(',');

  readonly discountTypeItems: DiscountTypeItem[] = ['rate', 'cash', 'tag'];

  form = new UntypedFormGroup(<TermsStepFormDataControls>{
    groups: this.formBuilder.array([], Validators.required),
  });

  override ngOnInit(): void {
    super.ngOnInit();

    this.emitForm(this.form);

    this.groups?.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      if (this.groups.controls.length > 1) {
        for (let i = 0; i < this.groups.controls.length; i++) {
          const groupNameControl = this.groups.at(i)?.get('groupName');

          if (!groupNameControl?.hasValidator(Validators.required)) {
            groupNameControl?.addValidators(Validators.required);
            groupNameControl?.updateValueAndValidity({ emitEvent: false });
          }
        }
      } else {
        const firstGroupNameControl = this.groups.at(0)?.get('groupName');
        firstGroupNameControl?.reset(null, { emitEvent: false });

        if (firstGroupNameControl?.hasValidator(Validators.required)) {
          firstGroupNameControl?.removeValidators(Validators.required);
        }

        firstGroupNameControl?.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  emitChanges() {
    const { groups } = this.form.value as TermsStepFormData;

    return {
      terms: groups.map(term => {
        let qrCodeImage: LoyaltyProgramProductTermQrCodeImage | undefined;
        let uniqueCodes: LoyaltyProgramProductTermUniqueCodes | undefined;

        if (term.qrCodeImage) {
          if (term.qrCodeImage instanceof File) {
            qrCodeImage = { file: term.qrCodeImage };
          } else {
            qrCodeImage = {
              id: term.qrCodeImage.id,
              file: term.qrCodeImage.name,
            };
          }
        }

        if (term.uniqueCodes) {
          if (term.uniqueCodes instanceof File) {
            uniqueCodes = { file: term.uniqueCodes };
          } else {
            uniqueCodes = {
              id: term.uniqueCodes.id,
              file: term.uniqueCodes.name,
            };
          }
        }

        return { ...term, qrCodeImage, uniqueCodes };
      }),
    };
  }

  get groups(): UntypedFormArray {
    return this.form.get('groups') as UntypedFormArray;
  }

  getCodeMode(isPermanentCode: boolean) {
    return isPermanentCode ? 0 : 1;
  }

  setCodeMode(group: AbstractControl<any, any>, activeItemIndex: number) {
    group.get('isPermanentCode')?.patchValue(activeItemIndex === 0);
  }

  getRejectedQrCodeImageControl(index: number) {
    return this.groups?.at(index).get('rejectedQrCodeImage');
  }

  addGroup(group?: LoyaltyProgramProductTerms) {
    let qrCodeImage: TuiFileLikeWithId | File | undefined;
    let uniqueCodes: TuiFileLikeWithId | File | undefined;
    if (group?.qrCodeImage) {
      if (group.qrCodeImage.file instanceof File) {
        qrCodeImage = group.qrCodeImage.file;
      } else {
        qrCodeImage = { id: group.qrCodeImage.id, name: group.qrCodeImage.file as string };
      }
    }

    if (group?.uniqueCodes) {
      if (group.uniqueCodes.file instanceof File) {
        uniqueCodes = group.uniqueCodes.file;
      } else {
        uniqueCodes = { id: group.uniqueCodes.id, name: group.uniqueCodes.file as string };
      }
    }

    const newGroup = this.formBuilder.group(
      <TermsStepFormGroupDataControls>{
        id: new UntypedFormControl(group?.id),
        groupName: new UntypedFormControl(group?.groupName, [Validators.maxLength(500)]),
        discountType: new UntypedFormControl(group?.discountType ?? 'rate', [Validators.required]),
        discount: new UntypedFormControl(group?.discount, [Validators.required]),
        description: new UntypedFormControl(group?.description ?? '', [
          Validators.required,
          Validators.maxLength(1000),
        ]),
        isPermanentCode: new UntypedFormControl(group?.isPermanentCode ?? true),
        isSingleCodeUse: new UntypedFormControl(group?.isSingleCodeUse ?? false),
        maxCodeUsePerMonth: new UntypedFormControl(group?.maxCodeUsePerMonth ?? 0, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        promoCode: new UntypedFormControl(group?.promoCode ?? '', [
          group?.showAsBarcode ? ean13Validator : Validators.maxLength(500),
        ]),
        showAsQRCode: new UntypedFormControl(group?.showAsQRCode ?? false),
        showAsBarcode: new UntypedFormControl(group?.showAsBarcode ?? false),
        qrCodeImage: new UntypedFormControl(qrCodeImage),
        rejectedQrCodeImage: new FormControl<Array<TuiFileLike>>([], {
          nonNullable: true,
          validators: [maxFilesLength(0)],
        }),
        uniqueCodes: new UntypedFormControl(uniqueCodes),
      },
      { validators: [validateQrCodeFields] },
    );

    newGroup
      .get('promoCode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(next => {
        if (next) {
          newGroup.get('qrCodeImage')?.reset();
        }
      });

    newGroup
      .get('qrCodeImage')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(next => {
        if (next) {
          newGroup.get('promoCode')?.reset();
        }
      });

    newGroup
      .get('isPermanentCode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(value => {
        const promoCodeControl = newGroup.get('promoCode');

        if (value) {
          if (newGroup.get('showAsBarcode')?.value) {
            promoCodeControl?.addValidators(ean13Validator);
          }
        } else {
          promoCodeControl?.reset();
          promoCodeControl?.removeValidators(ean13Validator);
        }

        newGroup?.updateValueAndValidity({ emitEvent: false });
      });

    newGroup
      .get('showAsQRCode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(next => {
        if (next) {
          newGroup.get('showAsBarcode')?.patchValue(false);
        }
      });

    newGroup
      .get('showAsBarcode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(next => {
        if (next) {
          if (newGroup.get('isPermanentCode')?.value) {
            newGroup.get('promoCode')?.addValidators(ean13Validator);
          }

          newGroup.get('showAsQRCode')?.patchValue(false);
        } else if (newGroup.get('promoCode')?.hasValidator(ean13Validator)) {
          newGroup.get('promoCode')?.removeValidators(ean13Validator);
        }

        newGroup.get('promoCode')?.updateValueAndValidity();
      });

    this.groups.push(newGroup);
  }

  removeGroup(index: number): void {
    this.groups.removeAt(index);
  }

  getDiscountTypeName(type: any): string {
    return this.translateService.instant(DISCOUNT_TYPE_NAMES[type as DiscountTypeItem]);
  }

  getDiscountType(index: number): DiscountTypeItem {
    const { groups } = this.form.value as TermsStepFormData;
    const { discountType } = groups[index];

    return discountType;
  }

  protected patchProduct({ terms = [] }: LoyaltyProgramProductInput) {
    this.form.patchValue({
      groups: terms.map(value => value),
    });

    if (terms.length > 0) {
      terms.forEach(term => this.addGroup(term));
    } else {
      this.addGroup();
    }
  }
}
